import { useFieldArray, useForm } from "react-hook-form";

import { useContext, useEffect, useCallback } from "react";

import ProposalContext from "modules/proposals/providers/ProposalContextProvider";

import PlantProductTransferTruckRow from "./PlantProductTransferTruckRow";
import useAddTruckToProposalProduct from "hooks/proposals/useAddTruckToProposalProduct";
import useRemoveTruckFromProposalProduct from "hooks/proposals/useRemoveTruckFromProposalProduct";
import {
  AddTruckToProposalProductInput,
  ProposalDefault,
  ProposalPlantProduct,
} from "generated/graphql";

interface PlantProductTransferTrucksTableBodyProps {
  transferTrucksFields: any[];
  product: any;
  proposalPlantProduct: ProposalPlantProduct;
  productTransferTrucks: any[];
  averageWorkingHoursPerDay: number;
  proposalDefaults: ProposalDefault;
  jobDurationInDays: number;
  compactMode: boolean;
  showAdvancedColumns: boolean;
}

const PlantProductTransferTrucksTableBody = ({
  transferTrucksFields,
  product,
  proposalPlantProduct,
  productTransferTrucks,
  averageWorkingHoursPerDay,
  proposalDefaults,
  jobDurationInDays,
  compactMode,
  showAdvancedColumns,
}: PlantProductTransferTrucksTableBodyProps) => {
  const { control, reset } = useForm({
    defaultValues: {
      transferTrucks: transferTrucksFields,
    },
  });

  useEffect(() => reset({ transferTrucks: transferTrucksFields }), [transferTrucksFields]);

  const [addTruckToProposalProduct] = useAddTruckToProposalProduct();
  const [removeTruckFromProposalProduct] = useRemoveTruckFromProposalProduct();

  const handleSubmit = useCallback(
    (input: Omit<AddTruckToProposalProductInput, "proposalPlantProductId">) => {
      addTruckToProposalProduct({
        variables: {
          input: {
            ...input,
            proposalPlantProductId: proposalPlantProduct.id,
          },
        },
      });
    },
    [addTruckToProposalProduct, proposalPlantProduct.id]
  );

  const handleRemoveTruck = useCallback(
    (id: string) => {
      removeTruckFromProposalProduct({ variables: { id } });
    },
    [removeTruckFromProposalProduct]
  );

  const { fields } = useFieldArray({
    control,
    name: "transferTrucks",
  });

  const { inViewMode } = useContext(ProposalContext);

  return (
    <>
      {fields.map((row, i) => (
        <PlantProductTransferTruckRow
          row={row}
          idx={i}
          productTransferTrucks={productTransferTrucks}
          product={product}
          proposalPlantProduct={proposalPlantProduct}
          key={[
            proposalPlantProduct.id,
            row.transferTruckId,
            row.truckingProductionRate ?? "",
            row.quantityOverride ?? "",
            row.totalHaulingHoursOverride ?? "",
            row.rateOverride ?? "",
            row.costOverride ?? "",
          ].join(":")}
          inViewMode={inViewMode}
          handleSubmit={handleSubmit}
          handleRemoveTruck={handleRemoveTruck}
          averageWorkingHoursPerDay={averageWorkingHoursPerDay}
          proposalDefaults={proposalDefaults}
          jobDurationInDays={jobDurationInDays}
          compactMode={compactMode}
          showAdvancedColumns={showAdvancedColumns}
        />
      ))}
    </>
  );
};

export default PlantProductTransferTrucksTableBody;
